import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';
import Footer from './Footer';
import './SettingsPage.css';
import { useNavigate } from 'react-router-dom';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [imageLoadError, setImageLoadError] = useState(false);

  // 重置图片加载错误状态当用户改变时
  useEffect(() => {
    setImageLoadError(false);
  }, [user?.picture]);

  if (!user) {
    return (
      <div className="settings-page">
        <Header />
        <div className="settings-container">
          <div className="settings-content">
            <h1>Please Login First</h1>
            <p>You need to login to access the settings page.</p>
          </div>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      logout();
    }
  };

  const handleUpgradePlan = () => {
    // window.open('/pricing', '_blank');
    navigate('/pricing')
  };

  const handleBuyCredits = () => {
    navigate('/pricing')
    // window.open('/pricing', '_blank'); // Replace with actual credits purchase URL
  };

  const handleJoinDiscord = () => {
    window.open('https://discord.gg/glistar', '_blank'); // Replace with actual Discord invite
  };

  // 处理头像图片加载错误
  const handleImageError = () => {
    setImageLoadError(true);
  };

  // 检查是否应该显示Google头像
  const shouldShowGoogleAvatar = () => {
    return user?.picture && user?.isGoogleUser && !imageLoadError;
  };

  // 生成默认头像（使用英文名首字母）
  const getDefaultAvatar = () => {
    // 对于Google用户，优先使用Google提供的名称
    if (user?.isGoogleUser && user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    if (user?.displayName) {
      return user.displayName.charAt(0).toUpperCase();
    }
    if (user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // 获取显示名称
  const getDisplayName = () => {
    // 对于Google用户，优先使用Google提供的名称
    if (user?.isGoogleUser && user?.name) {
      return user.name;
    }
    return user?.displayName || user?.name || user?.email?.split('@')[0] || '用户';
  };

  return (
    <div className="settings-page">
      <Header />
      
      <div className="main-container">
        <div className="settings-content">
          {/* User Profile Section */}
          <div className="profile-section">
            <div className="profile-container">
              <div className="profile-image">
                {shouldShowGoogleAvatar() ? (
                  <img 
                    src={user.picture} 
                    alt={getDisplayName()}
                    className="avatar-image"
                    onError={handleImageError}
                    onLoad={() => setImageLoadError(false)}
                  />
                ) : (
                  <div className="avatar-placeholder">
                    {getDefaultAvatar()}
                  </div>
                )}
              </div>
              <div className="profile-info">
                <div className="user-email">
                  <h1 className="email-text">{user.email}</h1>
                </div>
                <button className="logout-button" onClick={handleLogout}>
                  Log out
                </button>
              </div>
            </div>
          </div>

          {/* Free Plan Section */}
          <div className="plan-section">
            <div className="plan-header">
              <div className="plan-info">
                <h2 className="plan-title">Free Plan</h2>
                <p className="plan-description">Unlock all features with a subscription</p>
              </div>
              <button className="upgrade-link" onClick={handleUpgradePlan}>
                Upgrade plan
              </button>
            </div>
            
            <div className="credits-section">
              <div className="credits-info">
                <div className="credits-details">
                  <h3 className="credits-title">5 credit</h3>
                  <p className="credits-description">Credits require an active subscription</p>
                </div>
                <button className="buy-credits-link" onClick={handleBuyCredits}>
                  Buy credits
                </button>
              </div>
              <div className="credits-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{width: '50%'}}></div>
                </div>
              </div>
            </div>
          </div>


          {/* Billing Section */}
          <div className="billing-section">
            <div className="billing-header">
              <h3 className="billing-title">Billing</h3>
            </div>
            <div className="billing-empty">
              <div className="empty-icon">
                <svg width="48" height="72" viewBox="0 0 48 72" fill="none">
                  <path d="M24 8L40 16V56L24 64L8 56V16L24 8Z" fill="#4A5568" opacity="0.3"/>
                  <path d="M16 24H32V32H16V24Z" fill="#4A5568" opacity="0.5"/>
                  <path d="M16 36H28V40H16V36Z" fill="#4A5568" opacity="0.5"/>
                </svg>
              </div>
              <h4 className="empty-title">Nothing here yet</h4>
              <p className="empty-description">
                All your invoices will appear here once they're available.
              </p>
            </div>
          </div>

          {/* Discord Section */}
          <div className="discord-section">
            <div className="discord-info">
              <div className="discord-header">
                <h3 className="discord-title">Join our Discord</h3>
                <div className="discord-icon">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M16.942 4.28a16.3 16.3 0 0 0-4.126-1.3 12.04 12.04 0 0 0-.529.92 15.01 15.01 0 0 0-4.573 0 11.4 11.4 0 0 0-.535-.92 16.26 16.26 0 0 0-4.129 1.3A17.18 17.18 0 0 0 .182 13.218a15.9 15.9 0 0 0 4.963 2.521c.41-.564.773-1.16 1.084-1.785a10.63 10.63 0 0 1-1.706-.83c.143-.106.283-.217.418-.33a11.664 11.664 0 0 0 10.118 0c.135.113.275.224.418.33-.541.326-1.114.606-1.71.83.311.625.674 1.221 1.084 1.785a15.85 15.85 0 0 0 4.963-2.521c.297-3.036-.498-5.674-2.112-8.016zm-7.31 6.39c-.708 0-1.29-.656-1.29-1.456 0-.8.570-1.456 1.29-1.456.72 0 1.296.656 1.29 1.456 0 .8-.57 1.456-1.29 1.456zm4.735 0c-.708 0-1.29-.656-1.29-1.456 0-.8.57-1.456 1.29-1.456.72 0 1.297.656 1.29 1.456 0 .8-.57 1.456-1.29 1.456z" fill="currentColor"/>
                  </svg>
                </div>
              </div>
              <p className="discord-description">
                Get support, updates, and share your work<br />
                with the community!
              </p>
            </div>
            <button className="join-discord-link" onClick={handleJoinDiscord}>
              Join now
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SettingsPage;
